import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, get } from '@ember/object';
import { addObserver, removeObserver } from '@ember/object/observers';
import { stubTrue } from 'lodash';
import { next, scheduleOnce } from '@ember/runloop';
import Scrolling from 'mewe/utils/scrolling-utils';
import PS from 'mewe/utils/pubsub';
import { postsPerPage, FeedTypes, Theme } from 'mewe/constants';
import { checkOptions } from 'mewe/utils/component-utils';
import { getPublicProfileParams } from 'mewe/utils/miscellaneous-utils';

import dispatcher from 'mewe/dispatcher';
import { tracked } from '@glimmer/tracking';
import { isPresent } from '@ember/utils';
import { A } from '@ember/array';
import PostApi from 'mewe/api/post-api';
import { debounce } from '@ember/runloop';
import { restartableTask } from 'ember-concurrency';
const initialRenderPostsCount = 3;

const render = (eItem) => eItem && stubTrue(eItem.set('placeholder', false));

const placeholder = (eItem) => eItem && eItem.set('placeholder', true);

export default class FeedSimple extends Component {
  options = {
    mandatory: ['feed', 'theme'],
    optional: [
      'isPublicContent',
      'featuredFirst',
      'feedInHome',
      'eventData',
      'group',
      'groupId',
      'page',
      'getScrollContainer',
      'openHashTagFromTop',
      'isWrappersDialog',
      'tag',
      'contact',
      'classNames',
    ],
  };

  @service router;
  @service account;
  @service dynamicDialogs;
  @service features;

  @tracked currentTag = `#${this.args.tag}`;
  @tracked tagSuggestions = A();
  @tracked isNsfwFeedRevealed = false;

  scrolling = Scrolling();

  constructor() {
    super(...arguments);
    checkOptions(this.args.classNames || [], this.options || [], this.attrs);
  }

  @action
  onInsert() {
    scheduleOnce('afterRender', this, () => {
      if (this.isDestroyed || this.isDestroying) return;
      this.renderNextOrEnd();
    });

    this.setupInfiniteScroll();
    this.setupPendingPostsCheck();

    this.postsLengthObserverBind = this.postsLengthObserver.bind(this);
    addObserver(this, 'args.feed.posts.length', this.postsLengthObserverBind);
    this.initialRender();

    this.refreshPostfeedBind = this.refreshPostfeed.bind(this);
    PS.Sub('refresh.postfeed', this.refreshPostfeedBind);
  }

  @action
  onDestroy() {
    if (typeof this.args.getScrollContainer === 'function') {
      this.scrolling.unbindScrollDown(this.args.getScrollContainer());
    } else {
      this.scrolling.unbindScrollDown();
    }

    this.scrollingObserved.forEach((field) => removeObserver(this, field, this.infiniteScrollDownWrapped));
    this.pendingPostsObserved.forEach((field) => removeObserver(this, field, this.pendingPostsCheckWrapped));

    removeObserver(this, 'args.feed.posts.length', this.postsLengthObserverBind);
    PS.Unsub('refresh.postfeed', this.refreshPostfeedBind);
  }

  isCommentAction(target) {
    return (
      target.closest('.voice_buttons') ||
      target.closest('.comment-your_btn-cancel') ||
      target.closest('.comment-your_btn') ||
      target.closest('.comment-your_placeholder') ||
      target.closest('.postbar_comment') ||
      target.closest('.comment-your_right') ||
      target.closest('.comment-options-wrapper') ||
      target.closest('.comment_reply') ||
      target.closest('.comment-delete-option') ||
      target.closest('.comment-options') ||
      target.closest('.edit-form_buttons') ||
      target.closest('.editor-send-btn') ||
      target.closest('.c-mw-edit-form')
    );
  }

  get feed() {
    return this.args.feed;
  }

  get rendered() {
    return this.feed.posts.filterBy('placeholder', false);
  }

  get placeholders() {
    return this.feed.posts.filterBy('placeholder', true);
  }

  get isFailover() {
    return this.feed?.safeMode && !this.feed?.posts?.length;
  }

  get feedRoute() {
    if (this.args.group?.id) {
      return { name: 'app.group.index.feed', id: this.args.group.id };
    } else if (this.args.eventData?.id) {
      return { name: 'app.event.feed', id: this.args.eventData.id };
    }
  }

  get feedPendingRoute() {
    if (this.args.group?.id) {
      return { name: 'app.group.index.feed-pending', id: this.args.group.id };
    } else if (this.args.eventData?.id) {
      return { name: 'app.event.feed-pending', id: this.args.eventData.id };
    }
  }

  get sortedFeed() {
    // Doesn't need to be sorted, should order by the backend
    const dontSort = [FeedTypes.DISCOVER, FeedTypes.OPEN_FEED, FeedTypes.ALL, FeedTypes.ONE_FEED];
    if (dontSort.includes(this.args.theme)) {
      return this.feed.posts.uniqBy('id');
    }

    return this.args.featuredFirst ? this.feed.sortedPostsFeaturedFirst : this.feed.sortedPosts;
  }

  get hasPendingPosts() {
    const F = FeedTypes;

    if ([F.GROUP_CONTACT, F.DISCOVER].indexOf(this.feed.type) > -1) return false;
    if (this.args.tag) return false;

    if (this.args.group?.id) {
      return get(this, 'args.group.hasPendingPosts');
    } else if (this.args.eventData?.id) {
      return get(this, 'args.eventData.hasPendingPosts');
    }
  }

  get isMyProfile() {
    return this.args.theme === 'profile' && this.args.contact?.id === this.account.activeUser.id;
  }

  get isNsfwFeedWarning() {
    const isNsfwProfile = this.args.page?.nsfw || this.args.contact?.nsfw;
    return isNsfwProfile && !this.isMyProfile && !this.isNsfwFeedRevealed;
  }

  get showHashtagSuggestions() {
    return this.tagSuggestions.length > 0;
  }

  get feedTypeEnabled() {
    return this.features.isEnabled('web__is_one_feed_enabled') ? FeedTypes.ONE_FEED : FeedTypes.ALL;
  }

  setupInfiniteScroll() {
    // reference those fields as `args.feed` because observing simply `feed` doesn't work as expected
    this.scrollingObserved = ['args.feed.posts.length', 'args.feed.nextPage', 'args.feed.id'];
    // scheduleOnce will trigger callback only once even if multiple observser fields are changed at once
    this.infiniteScrollDownWrapped = () => scheduleOnce('afterRender', this, this.infiniteScrollDown);
    this.scrollingObserved.forEach((property) => addObserver(this, property, this.infiniteScrollDownWrapped));
    this.infiniteScrollDown();
  }

  setupPendingPostsCheck() {
    // reference those fields as `args.feed` because observing simply `feed` doesn't work as expected
    this.pendingPostsObserved = ['args.feed.posts.length', 'args.feed.isFetching', 'args.feed.videoJobsLoaded'];
    // scheduleOnce will trigger callback only once even if multiple observser fields are changed at once
    this.pendingPostsCheckWrapped = () => scheduleOnce('afterRender', this, this.pendingPostsCheck);
    this.pendingPostsObserved.forEach((property) => addObserver(this, property, this.pendingPostsCheckWrapped));
    this.pendingPostsCheck();
  }

  infiniteScrollDown() {
    const loadFn = () => {
      if (this.isDestroyed || this.isDestroying) return;
      this.loadMorePosts();
    };

    if (typeof this.args.getScrollContainer === 'function') {
      this.scrolling.bindScrollDownElement(this.args.getScrollContainer(), loadFn, 1200);
    } else {
      this.scrolling.bindScrollDown(() => loadFn(), 1200);
    }
  }

  // if route with pending feed was opened and fetching feed didn't return any posts then redirect to all posts feed and hide "all/pending" tabs
  // same if last post in pending feed will be removed/rejected/approved, don't stay in empty pending feed but redirect
  pendingPostsCheck() {
    // feed might not be passed yet during initialization (SG-32430)
    if (!this.feed) return;

    if (
      (this.feed.type == FeedTypes.GROUP_PENDING || this.feed.type == FeedTypes.EVENT_PENDING) &&
      this.feed.posts.length === 0 &&
      !this.feed.isFetching &&
      this.feed.videoJobsLoaded
    ) {
      if (this.feed.nextPage) {
        dispatcher.dispatch('feed', 'loadMore', this.feed);
      } else if (this.args.group?.id) {
        this.args.group.set('hasPendingPosts', false);
        this.router.transitionTo('app.group.index.feed');
      } else {
        this.args.eventData.set('hasPendingPosts', false);
        this.router.transitionTo('app.event.feed');
      }
    }
  }

  sliceMap(from, to, fun) {
    return this.feed.posts.slice(from, to).map(fun);
  }

  initialRender() {
    const length = this.feed.posts.length;
    if (length <= postsPerPage) {
      this.sliceMap(0, initialRenderPostsCount, render);
      this.sliceMap(initialRenderPostsCount, length, placeholder);
    }
  }

  postsLengthObserver() {
    const postsLength = this.feed.posts.length;

    this.feed.posts.map((post, index, posts) => (post.scheduled ? posts.removeAt(index) : null));

    if (postsLength && postsLength < postsPerPage && this.feed.canShowMore) {
      // SG-24048 - there is another page to load without scrolling
      dispatcher.dispatch('feed', 'loadMore', this.feed);
    } else {
      this.renderNextOrEnd();
    }
  }

  refreshPostfeed() {
    dispatcher.dispatch('feed', 'refreshPostfeed', this.feed);
  }

  renderNextOrEnd() {
    return this.rendered.length < this.feed.posts.length
      ? next(this, this.renderFirstFromPlaceholders)
      : this.afterAllRendered();
  }

  renderFirstFromPlaceholders() {
    return (
      !this.isDestroyed &&
      !this.isDestroying &&
      this.placeholders.length &&
      render(this.placeholders[0]) &&
      this.renderNextOrEnd()
    );
  }

  // posts are rendered each in different run loop, this is called in the end
  afterAllRendered() {
    // pre-rendering emoji picker, rendering of it is heavy and takes a lot of time (on 5x slowdown it takes 5s) so move to the very last
    // EmojiPicker(getOwner(this));//
    this.sliceMap(0, this.feed.posts.length, render);
  }

  @action
  goToLogin() {
    this.router.transitionTo('login', { queryParams: getPublicProfileParams(this.args.model) });
  }

  @action
  loadMorePosts() {
    if (this.feed?.canShowMore && !this.feed?.isFetching && !this.feed?.safeMode) {
      dispatcher.dispatch('feed', 'loadMore', this.feed);
    }
  }

  @action
  openHashTagFromTop(tag, scopeId, post) {
    if (this.args.openHashTagFromTop) {
      this.args.openHashTagFromTop(tag, scopeId, post);
    } else {
      this.router.transitionTo({ queryParams: { tag: tag || null } });
    }
  }

  @action
  updateTag() {
    this.currentTag = `#${this.args.tag}`;
  }

  @action
  handleHashtagInput(event) {
    const tag = this.currentTag.replace('#', '');
    if (event.key === 'Enter' || event.code === 'Enter' || event.keyCode === 13) {
      this.clearHashtagSuggestions();
      this.openHashTagFromTop(tag, null, null);
    } else {
      debounce(this, this.loadHashtagSuggestions.perform, tag, 500);
    }
  }

  @restartableTask
  *loadHashtagSuggestions(tag) {
    if (isPresent(tag)) {
      this.tagSuggestions.clear();
      const response = yield PostApi.getHashTags({ term: tag }, Theme.HOME, null);
      this.tagSuggestions.pushObject({ name: tag, total: 0 });
      this.tagSuggestions.pushObjects(response.hashtags);
    } else {
      this.tagSuggestions.clear();
    }
  }

  @action
  clearHashtagSuggestions() {
    this.tagSuggestions.clear();
  }

  @action
  selectHashtag(tag) {
    this.currentTag = `#${tag}`;
    this.clearHashtagSuggestions();
    this.openHashTagFromTop(tag, null, null);
  }

  @action
  closeHashtagBar() {
    this.currentTag = '';
    this.clearHashtagSuggestions();
    this.openHashTagFromTop('', null, null);
  }
}
